/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { SetAvailabilitySchema } from '@therapy/shared-validators';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { useMyAvailability, useSetAvailability } from '../../lib/queries/availability.js';
import { minutesToTime, timeToMinutes } from '../../lib/utils/datetime.js';

/** Mon-first weekday order; value is the IST day-of-week index (0=Sun). */
const DAYS: readonly { readonly value: number; readonly key: string }[] = [
  { value: 1, key: 'mon' },
  { value: 2, key: 'tue' },
  { value: 3, key: 'wed' },
  { value: 4, key: 'thu' },
  { value: 5, key: 'fri' },
  { value: 6, key: 'sat' },
  { value: 0, key: 'sun' },
];

interface EditWindow {
  readonly key: string;
  readonly dayOfWeek: number;
  readonly start: string;
  readonly end: string;
}

const TIME_CLASS =
  'focus-ring rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900';

interface WindowRowProps {
  readonly window: EditWindow;
  readonly onChange: (key: string, field: 'start' | 'end', value: string) => void;
  readonly onRemove: (key: string) => void;
  readonly removeLabel: string;
}

function WindowRow({ window, onChange, onRemove, removeLabel }: WindowRowProps): JSX.Element {
  const setStart = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(window.key, 'start', e.target.value);
    },
    [onChange, window.key],
  );
  const setEnd = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(window.key, 'end', e.target.value);
    },
    [onChange, window.key],
  );
  const remove = useCallback(() => {
    onRemove(window.key);
  }, [onRemove, window.key]);

  return (
    <div className="flex items-center gap-2">
      <input type="time" value={window.start} onChange={setStart} className={TIME_CLASS} />
      <span className="text-slate-400">–</span>
      <input type="time" value={window.end} onChange={setEnd} className={TIME_CLASS} />
      <button
        type="button"
        onClick={remove}
        aria-label={removeLabel}
        className="focus-ring rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

interface DayRowProps {
  readonly label: string;
  readonly dayValue: number;
  readonly addLabel: string;
  readonly noneLabel: string;
  readonly removeLabel: string;
  readonly windows: readonly EditWindow[];
  readonly onAdd: (dayValue: number) => void;
  readonly onChange: (key: string, field: 'start' | 'end', value: string) => void;
  readonly onRemove: (key: string) => void;
}

function DayRow({
  label,
  dayValue,
  addLabel,
  noneLabel,
  removeLabel,
  windows,
  onAdd,
  onChange,
  onRemove,
}: DayRowProps): JSX.Element {
  const add = useCallback(() => {
    onAdd(dayValue);
  }, [onAdd, dayValue]);
  return (
    <div className="flex items-start gap-4 border-b border-slate-100 py-3 last:border-0">
      <p className="w-24 pt-1 text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
      <div className="flex flex-1 flex-col gap-2">
        {windows.length === 0 ? (
          <p className="pt-1 text-sm text-slate-400">{noneLabel}</p>
        ) : (
          windows.map((w) => (
            <WindowRow
              key={w.key}
              window={w}
              onChange={onChange}
              onRemove={onRemove}
              removeLabel={removeLabel}
            />
          ))
        )}
        <button
          type="button"
          onClick={add}
          className="focus-ring inline-flex w-fit items-center gap-1 text-sm font-medium text-[var(--accent-strong)]"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {addLabel}
        </button>
      </div>
    </div>
  );
}

/** Therapist → Availability (P1-11). Manage recurring weekly working windows. */
export function AvailabilityPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useMyAvailability();
  const save = useSetAvailability();
  const [windows, setWindows] = useState<readonly EditWindow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const counter = useRef(0);
  const nextKey = useCallback(() => {
    counter.current += 1;
    return `w${counter.current.toString()}`;
  }, []);

  useEffect(() => {
    if (query.data !== undefined) {
      setWindows(
        query.data.map((w) => ({
          key: nextKey(),
          dayOfWeek: w.dayOfWeek,
          start: minutesToTime(w.startMinute),
          end: minutesToTime(w.endMinute),
        })),
      );
    }
  }, [query.data, nextKey]);

  const addFor = useCallback(
    (dayOfWeek: number) => {
      setWindows((prev) => [...prev, { key: nextKey(), dayOfWeek, start: '09:00', end: '17:00' }]);
    },
    [nextKey],
  );
  const changeWindow = useCallback((key: string, field: 'start' | 'end', value: string) => {
    setWindows((prev) => prev.map((w) => (w.key === key ? { ...w, [field]: value } : w)));
  }, []);
  const removeWindow = useCallback((key: string) => {
    setWindows((prev) => prev.filter((w) => w.key !== key));
  }, []);

  const onSave = useCallback(() => {
    const slots = windows.map((w) => ({
      dayOfWeek: w.dayOfWeek,
      startMinute: timeToMinutes(w.start),
      endMinute: timeToMinutes(w.end),
    }));
    const parsed = SetAvailabilitySchema.safeParse({ slots });
    if (!parsed.success) {
      setError(t('therapist.availability.invalid'));
      return;
    }
    setError(null);
    save.mutate(slots);
  }, [windows, save, t]);

  return (
    <div className="p-8">
      <PageHeader
        title={t('therapist.availability.title')}
        subtitle={t('therapist.availability.subtitle')}
        action={
          <Button onClick={onSave} disabled={save.isPending}>
            {t('therapist.availability.save')}
          </Button>
        }
      />
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
          {DAYS.map((day) => (
            <DayRow
              key={day.key}
              label={t(`therapist.availability.days.${day.key}`)}
              dayValue={day.value}
              addLabel={t('therapist.availability.addWindow')}
              noneLabel={t('therapist.availability.none')}
              removeLabel={t('therapist.availability.removeWindow')}
              windows={windows.filter((w) => w.dayOfWeek === day.value)}
              onAdd={addFor}
              onChange={changeWindow}
              onRemove={removeWindow}
            />
          ))}
        </div>
        {error !== null && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
        {save.isError && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {t('therapist.availability.saveError')}
          </p>
        )}
        {save.isSuccess && error === null && (
          <p className="mt-3 text-sm text-emerald-600">{t('therapist.availability.saved')}</p>
        )}
      </QueryState>
    </div>
  );
}
